import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { ArrowLeft, User, Shield, FileText, MessageSquare, AlertCircle } from "lucide-react";

interface CoachDetail {
  coach: { id: number; firstName: string; lastName: string; sport: string; title: string; email: string; phone: string | null; status: string };
  contracts: { id: number; contractType: string; documentKind: string; status: string; lifecycleState: string; startDate: string | null; endDate: string | null; createdAt: string }[];
  insurance: { id: number; policyType: string; carrier: string; premium: string | null }[];
  comments: { id: number; authorName: string; body: string; createdAt: string }[];
}

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function AdminCoachDetail() {
  const params = useParams<{ id: string; coachId: string }>();
  const [, setLocation] = useLocation();
  const [data, setData] = useState<CoachDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(`/api/admin/orgs/${params.id}/coaches/${params.coachId}`, { credentials: "include" });
        if (res.status === 401) { setLocation("/admin/login"); return; }
        if (res.status === 404) { setError("Coach not found in this organization."); return; }
        if (!res.ok) { setError("Failed to load coach."); return; }
        setData(await res.json() as CoachDetail);
      } catch { setError("Failed to load coach."); }
    })();
  }, [params.id, params.coachId, setLocation]);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link href={`/admin/orgs/${params.id}`} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to organization
        </Link>

        {error && (
          <div className="text-center py-12">
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-3" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {data && (
          <div className="space-y-5">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex items-start gap-4">
              <User className="h-6 w-6 text-blue-400 mt-1" />
              <div className="flex-1">
                <h1 className="text-xl font-bold">{data.coach.firstName} {data.coach.lastName}</h1>
                <p className="text-sm text-gray-400">{data.coach.title} · {data.coach.sport} · status {data.coach.status}</p>
                <p className="text-xs text-gray-500 mt-1">{data.coach.email}{data.coach.phone ? ` · ${data.coach.phone}` : ""}</p>
              </div>
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                <FileText className="h-4 w-4" /><h2 className="text-sm font-semibold">Contracts ({data.contracts.length})</h2>
              </div>
              {data.contracts.length === 0 ? (
                <p className="px-5 py-3 text-sm text-gray-500">No contracts.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-800">{["Type", "Kind", "Status", "Stage", "Effective", "Expires"].map((h) => <th key={h} className="text-left px-4 py-2 text-xs text-gray-500">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.contracts.map((c) => (
                      <tr key={c.id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                        <td className="px-4 py-2 text-gray-200 capitalize">
                          <Link href={`/admin/orgs/${params.id}/contracts/${c.id}`} className="hover:underline">{c.contractType.replace(/_/g, " ")}</Link>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-400">{c.documentKind}</td>
                        <td className="px-4 py-2 text-xs">{c.status}</td>
                        <td className="px-4 py-2 text-xs capitalize">{c.lifecycleState.replace(/_/g, " ")}</td>
                        <td className="px-4 py-2 text-xs text-gray-400">{fmt(c.startDate)}</td>
                        <td className="px-4 py-2 text-xs text-gray-400">{fmt(c.endDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                <Shield className="h-4 w-4" /><h2 className="text-sm font-semibold">Insurance ({data.insurance.length})</h2>
              </div>
              {data.insurance.length === 0 ? (
                <p className="px-5 py-3 text-sm text-gray-500">No insurance policies.</p>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {data.insurance.map((i) => (
                    <li key={i.id} className="px-5 py-2 text-sm flex justify-between">
                      <span className="text-gray-200">{i.policyType} — {i.carrier}</span>
                      <span className="text-xs text-gray-500">{i.premium ? `$${Number(i.premium).toLocaleString()}/yr` : "—"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800">
                <MessageSquare className="h-4 w-4" /><h2 className="text-sm font-semibold">Comments ({data.comments.length})</h2>
              </div>
              {data.comments.length === 0 ? (
                <p className="px-5 py-3 text-sm text-gray-500">No comments.</p>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {data.comments.map((c) => (
                    <li key={c.id} className="px-5 py-3 text-sm">
                      <p className="text-gray-200">{c.body}</p>
                      <p className="text-xs text-gray-500 mt-1">— {c.authorName} · {new Date(c.createdAt).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
